import { ChevronDown } from 'lucide-react';
import React, { useId, useRef, useState } from 'react';

import { useClickOutside } from 'hooks/useClickOutside';

interface Option {
  key: string;
  label: string;
}

interface SearchSelectProps {
  value?: string;
  options: Option[];
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: React.ReactNode;
  variant?: 'input' | 'text';
  label?: string;
  disabled?: boolean;
  noBorder?: boolean;
}

export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = 'Select an option',
  icon,
  variant = 'input',
  label,
  disabled = false,
  noBorder = false,
}: SearchSelectProps) {
  const panelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setIsOpen(false));

  const selectedOption = value ? options.find(opt => opt.key === value) : null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={isOpen ? panelId : undefined}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-1 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        } ${
          variant === 'input'
            ? noBorder
              ? 'h-12 w-full px-4 py-3 text-left'
              : 'h-12 w-full rounded-md border bg-white px-4 py-3 text-left'
            : 'text-sm'
        }`}
      >
        {label && (
          <span className="inline-flex items-center text-gray-600">
            {label}
          </span>
        )}
        {icon}
        <span
          className={`flex-1 ${
            variant === 'input'
              ? 'text-gray-900'
              : 'text-primary-600 hover:text-primary-700'
          }`}
        >
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          className={`${
            variant === 'input' ? 'h-5 w-5 text-gray-400' : 'h-4 w-4'
          } transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          id={panelId}
          className="absolute z-50 mt-1 w-full min-w-[8rem] rounded-lg border border-gray-100 bg-white py-2 shadow-lg"
        >
          {options.map(option => (
            <button
              key={option.key}
              type="button"
              onClick={() => {
                onChange(option.key);
                setIsOpen(false);
              }}
              className="flex w-full px-4 py-3 text-start text-sm text-gray-800 hover:bg-gray-50"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchSelect;
